import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { healthCommitteeApi } from "../../services/healthCommitteeApi";
import { employeeApi } from "../../services/employeeApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ReasonDialog } from "../../components/ReasonDialog";
import { NotFoundState } from "../../components/NotFoundState";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const EDITABLE_STATUSES = ["DRAFT", "REVIEW_REQUIRED", "REOPENED"];

export default function HealthCommitteeMeetingDetailPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageHealthCommittee } = usePermissions();
  const [meeting, setMeeting] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [draft, setDraft] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [newAction, setNewAction] = useState({ description: "", responsible_employee_id: "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // "reopen" | "cancel"

  const FIELD_LABELS = {
    discussion_summary: t("mgmtHealthCommittee.discussionSummary"),
    decisions: t("mgmtHealthCommittee.decisions"),
    minutes_text: t("mgmtHealthCommittee.minutesText"),
  };

  const load = useCallback(() => {
    healthCommitteeApi.getMeeting(meetingId).then((r) => { setMeeting(r.data); setDraft(r.data); setNotFound(false); }).catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
    });
    healthCommitteeApi.listActionItems({ meeting_id: meetingId }).then((r) => setActionItems(r.data)).catch(() => {});
  }, [meetingId]);
  useEffect(() => { load(); employeeApi.list().then((r) => setEmployees(r.data)).catch(() => {}); }, [load]);

  if (notFound) return <NotFoundState testId="health-committee-meeting-not-found" backTo="/management/compliance/health-committee/meetings" />;
  if (!meeting || !draft) return <p className="text-sm text-gray-500" data-testid="health-committee-meeting-loading">{t("common.loading")}</p>;

  const isEditable = canManageHealthCommittee && EDITABLE_STATUSES.includes(meeting.status);
  const nameOf = (id) => employees.find((e) => e.id === id)?.legal_name || id;

  const run = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); toast.success(successMsg || t("mgmtHealthCommittee.updated")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveDraft = () => run(() => healthCommitteeApi.updateMeeting(meetingId, {
    meeting_date: draft.meeting_date, start_time: draft.start_time, end_time: draft.end_time, location: draft.location,
    meeting_type: draft.meeting_type, chairperson_employee_id: draft.chairperson_employee_id, recorder_employee_id: draft.recorder_employee_id,
    attendees: draft.attendees, absent_members: draft.absent_members, agenda: draft.agenda,
    discussion_summary: draft.discussion_summary, decisions: draft.decisions, minutes_text: draft.minutes_text,
  }), t("mgmtHealthCommittee.meetingSaved"));

  const toggleAttendance = (empId, list) => {
    const other = list === "attendees" ? "absent_members" : "attendees";
    setDraft((d) => {
      const current = new Set(d[list] || []);
      if (current.has(empId)) current.delete(empId); else current.add(empId);
      const otherSet = new Set(d[other] || []);
      otherSet.delete(empId);
      return { ...d, [list]: Array.from(current), [other]: Array.from(otherSet) };
    });
  };

  const addAgendaItem = () => setDraft((d) => ({ ...d, agenda: [...(d.agenda || []), { topic: "", presenter: "", notes: "" }] }));
  const updateAgendaItem = (idx, field, value) => setDraft((d) => ({ ...d, agenda: d.agenda.map((a, i) => (i === idx ? { ...a, [field]: value } : a)) }));
  const removeAgendaItem = (idx) => setDraft((d) => ({ ...d, agenda: d.agenda.filter((_, i) => i !== idx) }));

  const createActionItem = async () => {
    if (!newAction.description) return;
    await run(() => healthCommitteeApi.createActionItem(meetingId, { ...newAction, responsible_employee_id: newAction.responsible_employee_id || null, due_date: newAction.due_date || null }), t("mgmtHealthCommittee.actionItemCreated"));
    setNewAction({ description: "", responsible_employee_id: "", due_date: "" });
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="health-committee-meeting-detail-page">
      <PageHelpBanner pageKey="health-committee-meeting-detail" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtHealthCommittee.meetingDetailHelp") }} />
      </PageHelpBanner>
      <Link to="/management/compliance/health-committee/meetings" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="health-committee-back-link">&larr; {t("mgmtHealthCommittee.backToMeetings")}</Link>

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-xl font-bold text-gray-900" data-testid="health-committee-meeting-title">{t("mgmtHealthCommittee.meeting")} - {meeting.meeting_date}</h2>
          <StatusBadge status={meeting.status} />
        </div>

        {isEditable ? (
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs text-gray-500 mb-1">{t("common.date")}</label>
              <input data-testid="hc-edit-date-input" type="date" value={draft.meeting_date || ""} onChange={(e) => setDraft((d) => ({ ...d, meeting_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.colLocation")}</label>
              <input data-testid="hc-edit-location-input" value={draft.location || ""} onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-500">{meeting.meeting_type} - {meeting.location || t("mgmtHealthCommittee.noLocationRecorded")}</p>
        )}

        <div>
          <h3 className="text-xs font-bold uppercase text-gray-500 mb-2">{t("mgmtHealthCommittee.agenda")}</h3>
          {(draft.agenda || []).map((a, idx) => (
            <div key={idx} className="flex gap-2 mb-2" data-testid={`hc-agenda-item-${idx}`}>
              {isEditable ? (
                <>
                  <input data-testid={`hc-agenda-topic-${idx}`} value={a.topic} onChange={(e) => updateAgendaItem(idx, "topic", e.target.value)} placeholder={t("mgmtHealthCommittee.topic")} className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
                  <input data-testid={`hc-agenda-presenter-${idx}`} value={a.presenter || ""} onChange={(e) => updateAgendaItem(idx, "presenter", e.target.value)} placeholder={t("mgmtHealthCommittee.presenter")} className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
                  <button data-testid={`hc-agenda-remove-${idx}`} onClick={() => removeAgendaItem(idx)} className="text-xs text-red-500 px-2">✕</button>
                </>
              ) : (
                <p className="text-sm text-gray-700">{a.topic} {a.presenter && `(${a.presenter})`}</p>
              )}
            </div>
          ))}
          {isEditable && <button data-testid="hc-add-agenda-item-btn" onClick={addAgendaItem} className="text-xs font-semibold text-blue-600 hover:underline">+ {t("mgmtHealthCommittee.addAgendaItem")}</button>}
          {(!draft.agenda || draft.agenda.length === 0) && !isEditable && <p className="text-xs text-gray-500" data-testid="hc-no-agenda">{t("mgmtHealthCommittee.noAgendaRecorded")}</p>}
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase text-gray-500 mb-2">{t("mgmtHealthCommittee.attendance")}</h3>
          {isEditable ? (
            <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-lg p-2 grid grid-cols-2 gap-1">
              {employees.map((e) => (
                <label key={e.id} className="flex items-center gap-1.5 text-xs text-gray-600" data-testid={`hc-attendance-checkbox-${e.id}`}>
                  <input type="checkbox" checked={(draft.attendees || []).includes(e.id)} onChange={() => toggleAttendance(e.id, "attendees")} />
                  {e.legal_name}
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-700" data-testid="hc-attendees-readonly">
              {t("mgmtHealthCommittee.present")}: {(meeting.attendees || []).map(nameOf).join(", ") || "-"} <br />
              {t("mgmtHealthCommittee.absent")}: {(meeting.absent_members || []).map(nameOf).join(", ") || "-"}
            </p>
          )}
        </div>

        {["discussion_summary", "decisions", "minutes_text"].map((field) => (
          <div key={field}>
            <label className="block text-xs text-gray-500 mb-1">{FIELD_LABELS[field]}{field === "minutes_text" && " *"}</label>
            {isEditable ? (
              <textarea data-testid={`hc-${field}-input`} value={draft[field] || ""} onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            ) : (
              <p className="text-sm text-gray-700 whitespace-pre-line" data-testid={`hc-${field}-readonly`}>{meeting[field] || "-"}</p>
            )}
          </div>
        ))}

        {isEditable && (
          <button data-testid="hc-save-meeting-btn" disabled={busy} onClick={saveDraft} className="px-4 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50">
            {t("mgmtHealthCommittee.saveChanges")}
          </button>
        )}
      </div>

      {canManageHealthCommittee && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-wrap gap-2" data-testid="hc-lifecycle-actions">
          {["DRAFT", "REVIEW_REQUIRED", "REOPENED"].includes(meeting.status) && (
            <button data-testid="hc-submit-btn" disabled={busy} onClick={() => run(() => healthCommitteeApi.submitMeeting(meetingId), t("mgmtHealthCommittee.submitted"))} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t("mgmtHealthCommittee.submit")}</button>
          )}
          {meeting.status === "SUBMITTED" && (
            <button data-testid="hc-approve-btn" disabled={busy} onClick={() => run(() => healthCommitteeApi.approveMeeting(meetingId), t("mgmtHealthCommittee.approved"))} className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">{t("common.approve")}</button>
          )}
          {meeting.status === "APPROVED" && (
            <button data-testid="hc-finalize-btn" disabled={busy} onClick={() => run(() => healthCommitteeApi.finalizeMeeting(meetingId), t("mgmtHealthCommittee.finalizedToast"))} className="px-4 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50">{t("mgmtHealthCommittee.finalize")}</button>
          )}
          {["SUBMITTED", "APPROVED", "FINALIZED"].includes(meeting.status) && (
            <button data-testid="hc-reopen-btn" disabled={busy} onClick={() => setDialog("reopen")} className="px-4 py-2 rounded-lg text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50">{t("mgmtHealthCommittee.reopenEllipsis")}</button>
          )}
          {!["FINALIZED", "REPLACED", "CANCELLED"].includes(meeting.status) && (
            <button data-testid="hc-cancel-btn" disabled={busy} onClick={() => setDialog("cancel")} className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50">{t("mgmtHealthCommittee.cancelEllipsis")}</button>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">{t("mgmtHealthCommittee.actionItems")}</h3>
        {canManageHealthCommittee && (
          <div className="flex flex-wrap gap-2 items-end" data-testid="hc-new-action-form">
            <input data-testid="hc-new-action-description" value={newAction.description} onChange={(e) => setNewAction((a) => ({ ...a, description: e.target.value }))} placeholder={t("mgmtHealthCommittee.colDescription")} className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <select data-testid="hc-new-action-owner" value={newAction.responsible_employee_id} onChange={(e) => setNewAction((a) => ({ ...a, responsible_employee_id: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
              <option value="">{t("mgmtHealthCommittee.ownerEllipsis")}</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
            </select>
            <input data-testid="hc-new-action-due-date" type="date" value={newAction.due_date} onChange={(e) => setNewAction((a) => ({ ...a, due_date: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <button data-testid="hc-new-action-submit-btn" disabled={busy || !newAction.description} onClick={createActionItem} className="px-3 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t("mgmtHealthCommittee.add")}</button>
          </div>
        )}
        <div className="space-y-2">
          {actionItems.map((a) => (
            <div key={a.id} className="border-t border-gray-100 pt-2 flex items-center justify-between text-sm" data-testid={`hc-action-item-${a.id}`}>
              <span className="text-gray-700">{a.description} {a.responsible_employee_id && `- ${nameOf(a.responsible_employee_id)}`}</span>
              <StatusBadge status={a.status} />
            </div>
          ))}
          {actionItems.length === 0 && <p className="text-xs text-gray-500" data-testid="hc-no-action-items">{t("mgmtHealthCommittee.noActionItemsMeeting")}</p>}
        </div>
        <p className="text-xs text-gray-500">{t("mgmtHealthCommittee.actionItemsCrossBoard")}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("mgmtHealthCommittee.auditTimeline")}</h3>
        <div className="space-y-2">
          {(meeting.audit_history || []).slice().reverse().map((h, idx) => (
            <div key={idx} className="text-xs border-l-2 border-gray-200 pl-3 py-1" data-testid={`hc-audit-entry-${idx}`}>
              <span className="font-semibold text-gray-700">{h.action}</span> <span className="text-gray-500">{t("mgmtHealthCommittee.by")} {h.actor} {t("mgmtHealthCommittee.at")} {h.at}</span>
              {h.notes && <p className="text-gray-500 mt-0.5">{h.notes}</p>}
            </div>
          ))}
          {(!meeting.audit_history || meeting.audit_history.length === 0) && <p className="text-xs text-gray-500" data-testid="no-hc-audit-history">{t("mgmtHealthCommittee.noAuditHistory")}</p>}
        </div>
      </div>

      {dialog === "reopen" && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setDialog(null)} title={t("mgmtHealthCommittee.reopenMeetingTitle")}
          description={t("mgmtHealthCommittee.reopenMeetingDescription")}
          contextItems={[{ label: t("mgmtHealthCommittee.meetingDate"), value: meeting.meeting_date }, { label: t("mgmtHealthCommittee.currentStatus"), value: meeting.status }]}
          reasonLabel={t("mgmtHealthCommittee.reasonToReopen")} danger confirmLabel={t("mgmtHealthCommittee.reopenMeeting")}
          onConfirm={async (reason) => {
            const { data } = await healthCommitteeApi.reopenMeeting(meetingId, reason);
            if (data.id !== meetingId) {
              toast.success(t("mgmtHealthCommittee.reopenedAsReplacement"));
              navigate(`/management/compliance/health-committee/meetings/${data.id}`);
            } else {
              toast.success(t("mgmtHealthCommittee.reopened"));
              load();
            }
          }}
          testId="hc-reopen-dialog"
        />
      )}
      {dialog === "cancel" && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setDialog(null)} title={t("mgmtHealthCommittee.cancelMeetingTitle")}
          contextItems={[{ label: t("mgmtHealthCommittee.meetingDate"), value: meeting.meeting_date }, { label: t("mgmtHealthCommittee.currentStatus"), value: meeting.status }]}
          reasonLabel={t("mgmtHealthCommittee.reasonToCancel")} danger confirmLabel={t("mgmtHealthCommittee.cancelMeeting")}
          onConfirm={async (reason) => { await healthCommitteeApi.cancelMeeting(meetingId, reason); toast.success(t("mgmtHealthCommittee.cancelled")); load(); }}
          testId="hc-cancel-dialog"
        />
      )}
    </div>
  );
}
